import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Heart, 
  Activity, 
  AlertTriangle, 
  Zap,
  TrendingUp,
  TrendingDown,
  Wind,
  Droplet,
  FileText,
  Sparkles,
  Loader2,
  CheckCircle2,
  XCircle
} from 'lucide-react';

import { API_URL } from '../config';

export default function ReferentielsCardioPage() {
  const [aiLoading, setAiLoading] = useState({});
  const [aiResults, setAiResults] = useState({});

  const handleAiAnalysis = async (pathologyId, pathologyName, content) => {
    setAiLoading({ ...aiLoading, [pathologyId]: true });
    
    try {
      const response = await fetch(`${API_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          context: 'referentiels_cardio',
          audience: 'adult',
          prompt: `Analyse clinique pour ${pathologyName}:

${content.slice(0, 500)}

Fournis une analyse synthétique avec:
1. Points clés du diagnostic
2. Éléments de gravité à rechercher
3. Premiers gestes thérapeutiques
4. Pièges à éviter
5. Critères d'orientation (USIC/Réanimation/Bloc)`
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiResults({ ...aiResults, [pathologyId]: data.response });
        toast.success(`Analyse AI ${pathologyName} complétée`);
      } else {
        toast.error('Erreur lors de l\'analyse');
      }
    } catch (error) {
      console.error('AI Analysis error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setAiLoading({ ...aiLoading, [pathologyId]: false });
    }
  };

  const pathologies = [
    {
      id: 'sca-st-plus',
      name: 'SCA ST+',
      icon: Heart,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**Syndrome Coronarien Aigu avec sus-décalage du ST**

**DIAGNOSTIC:**
• Douleur thoracique persistante ≥ 30 min, trinitro résistante
• Dyspnée, palpitations, nausées, vomissements
• Asthénie, syncope

**ECG 18 DÉRIVATIONS (dans les 10 min):**
• ST+ sur au moins 2 dérivations contiguës:
  - H < 40 ans: ST+ ≥ 2,5 mm
  - H > 40 ans: ST+ ≥ 2 mm
  - Femme: ST+ ≥ 1,5 mm en V2V3 et/ou ≥ 1 mm ailleurs
• BBG ou BBD = même prise en charge urgente
• Sous-décalage ST >8 dérivations + ST+ en aVR = occlusion tronc/multi-vaisseaux (équivalent ST+)

**TRAITEMENT INITIAL:**
• Repos, VVP, scope, défibrillateur
• O2 si SaO2 < 90% ou PaO2 < 60 mmHg
• Antalgie: Paracétamol, Morphine en titration si nécessaire

**TRAITEMENTS SPÉCIFIQUES:**
• Dérivés nitrés: Natispray sublingual ou ISOSORBIDE DINITRATE (Risordan®) IVSE 1 mg/h jusqu'à 10 mg/h
• Aténolol (Tenormine®): 1 mg/min IVL jusqu'à 5 mg pour FC < 60 (si pas de CI)
• Aspirine: 250 mg IVD
• Héparine (HNF): 60 UI/kg BOLUS IVD (max 10 000 UI), puis IVSE 12 UI/kg/h (max 2000 UI/h)
• Ticagrelor (Brilique®): 2 cp (180 mg) per os

**REPERFUSION:**
• Coronarographie en urgence (< 90 min)
• Si délai > 120 min: Thrombolyse
  - Ténectéplase (TNK): Bolus unique selon poids
  - Alteplase: 0,9 mg/kg (max 90 mg): 10% bolus + 90% en 60 min

**SURVEILLANCE:**
• PA, FC, scope continu
• Recherche complications: troubles du rythme, IC, choc cardiogénique`
    },
    {
      id: 'sca-non-st',
      name: 'SCA NON ST+',
      icon: Activity,
      color: 'text-orange-400',
      bg: 'bg-orange-500/10',
      border: 'border-orange-400/30',
      content: `**Syndrome Coronarien Aigu sans sus-décalage du ST**

**DIAGNOSTIC:**
• Douleur coronarienne
• ABSENCE de sus-décalage ST sur ECG 18 dérivations
• Troponine positive

**CRITÈRES DE GRAVITÉ (→ avis cardio immédiat):**
• Insuffisance cardiaque
• Instabilité hémodynamique
• Persistance douleur malgré traitement optimal
• Troubles du rythme

**TRAITEMENT:**
• Repos, VVP, scope, défibrillateur
• O2 si SaO2 < 90% ou PaO2 < 60 mmHg
• Antalgie: Paracétamol, Morphine en titration

**MÉDICAMENTS:**
• Dérivés nitrés: ISOSORBIDE DINITRATE (Risordan®) IVSE 1 mg/h → 10 mg/h
• Aténolol (Tenormine®): 1 mg/min IVL jusqu'à 5 mg (si pas de CI)
• Aspirine: 250 mg IVD

**BI-ANTIAGRÉGATION & ANTICOAGULATION:**
⚠️ Avis cardio avant décision:
• Ticagrelor (Brilique®) vs Prasugrel vs Clopidogrel ?
• HNF vs Fondaparinux (Arixtra® 2,5 mg SC) ?

**TRANSPORT:**
• Si SCA ST- avec troponine+, sans signes ECG, patient asymptomatique:
  → Transport non médicalisé possible (ambulance DSA) après discussion régulation
• Si critères de gravité:
  → Transport médicalisé en USIC`
    },
    {
      id: 'complications-sca',
      name: 'Complications des SCA',
      icon: AlertTriangle,
      color: 'text-yellow-400',
      bg: 'bg-yellow-500/10',
      border: 'border-yellow-400/30',
      content: `**Complications post-SCA**

**1. BRADYCARDIE SINUSALE OU BAV:**
• Atropine 0,5 à 1 mg IVD (toutes les 3-5 min, max 3 mg)
• Entraînement ÉlectroSystolique Externe (EES)
• Si échec: discuter Adrénaline IVSE
⚠️ Rechercher cause curable: ischémie, dyskaliémie, cardiotropes, hypothermie, hypothyroïdie, hypoxie, hypovolémie

**2. FIBRILLATION AURICULAIRE:**
• Sans IC ni hypotension: Aténolol 1 mg/min (max 5 mg)
• IC sans hypotension: Amiodarone 5 mg/kg sur 20 min (max 300 mg)
• IC + hypotension: Digoxine 0,25-0,5 mg IVL (si kaliémie disponible)
• Cardioversion par CEE + amiodarone si échec

**3. TACHYCARDIE VENTRICULAIRE (TV):**
• Si bonne tolérance: avis cardio, si indication Aténolol 1 mg/min (max 5 mg)
• TV + choc cardiogénique avec troubles conscience ou ACR: CARDIOVERSION ÉLECTRIQUE
• TV + OAP/SCA: Amiodarone 5 mg/kg IV sur 20-60 min puis 50 mg/h (max <1g/24h)
• Sans critères CEE: Amiodarone 5 mg/kg IV sur 30-60 min
• Dernier recours: Lidocaïne 1-1,5 mg/kg IVD (~100 mg), réitérer 50 mg si besoin (max 3 mg/kg)

**4. TORSADES DE POINTE:**
**Diagnostic:**
• TV polymorphe avec rotation axiale en torsade
• Fréquence 200-250/min
• Changement d'axe tous les 10 complexes
• ESV sur T dans contexte QT long

**Traitement:**
• Sevrage médicament responsable
• Sulfate de Magnésium: 2g IVSE sur 10 min (si QTc > 500 ms)
• Potassium: objectif 4,5-5 mmol/L
• Si torsades récurrentes pauses-dépendantes: Isoprénaline
• Si bradycardie sévère: EESE

⚠️ QT long = facteur de risque majeur`
    },
    {
      id: 'acr',
      name: 'Arrêt Cardio-Respiratoire',
      icon: Zap,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**ACR Adulte et Enfant**

**RCP DE BASE:**
• LVAS: 5 insufflations si enfant
• MCE-VA: 30/2 adulte ou 15/2 enfant
• Défibrillateur dès que possible
• Maximiser le temps avec MCE

**TRAITER LES CAUSES CURABLES (5H-5T):**
• Hypoxie, Hypovolémie, Hypo/Hyperkaliémie, Hypothermie, H+ (acidose)
• Toxines (intox), Thrombose x2 (IDM-EP), Tamponnade, Pneumothorax

**ADULTE - RYTHME CHOQUABLE (FV/TVSP):**
1. CEE 1 immédiat (200 J biphasique)
2. RCP 2 min
3. CEE 2 (200 J)
4. RCP 2 min + ADRÉNALINE 1 mg IV/IO
5. CEE 3 (200 J) + AMIODARONE 300 mg IV/IO
6. RCP 2 min + ADRÉNALINE 1 mg
7. CEE 4
8. RCP 2 min
9. CEE 5 + AMIODARONE 150 mg
10. Continuer: ADRÉNALINE 1 mg toutes les 3-5 min

**ADULTE - RYTHME NON CHOQUABLE (Asystolie/RSP):**
• ADRÉNALINE 1 mg IV/IO immédiat
• RCP continue
• ADRÉNALINE 1 mg toutes les 3-5 min

**ENFANT:**
• ADRÉNALINE: 10 µg/kg IV/IO
• AMIODARONE: 5 mg/kg IV/IO
• CEE: 4 J/kg

**PENDANT LA RCP:**
• Oxygène + Capnographie
• MCE continu si voies aériennes sécurisées
• Accès vasculaire (IV ou IO)
• Envisager: Échographie, MCE mécaniques, coronarographie, ECMO

**HYPOTHERMIE (<32°C):**
• Réchauffement passif (+0,5°C/h)
• Si <30°C: Pas d'adrénaline (inefficace)
• À partir de 30°C: Adrénaline toutes les 6-10 min
• Si FV: Essai 3 CEE puis attendre >30°C
• Discuter ECMO

**APRÈS RACS:**
• Continuer MCE/VA pendant 2 min
• Réanimation post-RACS
• Prise en charge étiologique`
    },
    {
      id: 'oap',
      name: 'OAP Cardiogénique',
      icon: Wind,
      color: 'text-cyan-400',
      bg: 'bg-cyan-500/10',
      border: 'border-cyan-400/30',
      content: `**Œdème Aigu Pulmonaire Cardiogénique**

**DIAGNOSTIC CLINIQUE:**
• Insuffisance respiratoire aiguë brutale
• Dyspnée, tachypnée >20/min, toux, expectoration mousseuse, orthopnée
• Sueurs, agitation, angoisse
• Auscultation: tachycardie, HTA, râles crépitants (marée montante)
• Score Killip, galop B3, TJ, RHJ

**EXAMENS:**
• ECG 18D: signes ischémie, troubles rythme/conduction
• Écho pleurale: profil B (≥3 lignes B par espace, bilatéral)
• ETT: orientation étiologique si instabilité hémodynamique

**ÉLÉMENTS DE GRAVITÉ:**
• Choc cardiogénique (PAS <90 mmHg)
• FC <40/min ou >130/min
• Détresse respiratoire: cyanose, lutte, impossibilité parler, SpO2 <90%, FR >25 ou <12/min
• Troubles de conscience

**TRAITEMENT:**
• Position ½ assise
• O2 au MHC, QSP SpO2 ≥ 90%
• VVP garde veine
• Traitement étiologique (ischémie, tamponnade...)

**VASODILATATEURS (si pas d'hypotension, RAC, infarctus inférieur):**
Objectif: Réduction 25% PA en 1ère heure, PAS >90 mmHg
• Natispray en attendant VVP
• ISOSORBIDE DINITRATE (Risordan®) IVSE 1 mg/h → 10 mg/h, bolus 1 mg possibles

**DIURÉTIQUES:**
• Furosémide: 40 mg IVD (patient naïf) ou dose équivalente au traitement habituel

**CONTRÔLE TA:**
• Nicardipine (Loxen®) si PA non contrôlée: 1-2 mg IVD puis 2 mg/h +/- paliers 1 mg/h

**SÉDATION:**
• Si agitation: Morphine IVD 2-5 mg ou benzodiazépines selon cas

**VNI:**
• À débuter conjointement au traitement médical (respect CI)

**CRITÈRES IOT:**
• Troubles conscience
• Épuisement respiratoire
• Hypoxémie réfractaire
• Obstruction voies aériennes
• Troubles rythme ventriculaire grave
• Choc cardiogénique réfractaire

**ÉTIOLOGIES "CHAMP":**
• Coronaires, Hypertension, Arythmie, Mécanique, embolie Pulmonaire`
    },
    {
      id: 'choc-cardiogenique',
      name: 'Choc Cardiogénique',
      icon: AlertTriangle,
      color: 'text-orange-400',
      bg: 'bg-orange-500/10',
      border: 'border-orange-400/30',
      content: `**Choc Cardiogénique**

**DIAGNOSTIC CLINIQUE:**
• PAS <90 mmHg ou PAM <70 mmHg
• Tachycardie quasi constante, parfois troubles conduction
• Signes IC droite (RHJ, OMI) ou ICG (OAP, crépitants, orthopnée)
• Marbrures, sueurs froides, extrémités froides et cyanosées
• Angoisse, agitation, troubles conscience, obnubilation voire coma
• Oligurie

**DIAGNOSTIC ÉTIOLOGIQUE:**
• Anomalies fréquence: troubles rythme/conduction
• Anomalies précharge: cardiomyopathie ischémique/dilatée, tamponnade, PNO, IM, IAo
• Anomalies contractilité: IDM, myocardite, toxique (β-bloquants, IEC, tricycliques), tako tsubo
• Anomalies post-charge: EP, HTA, RAo

**PARACLINIQUE:**
• PA pincée: défaut d'éjection (hypovolémie ou défaillance cardiaque)
• Échocardiographie: ITV sous-aortique (N=14-20 cm), FEVG, étiologie
• ECG +++

**TRAITEMENT:**
**Objectif: PAM 65-70 mmHg**

• Restaurer pression perfusion:
  - NORADRÉNALINE 0,5-2 mg/h IVSE
  - Augmenter par paliers 0,2-0,5 mg/h toutes les 15 min

• Traiter bas débit cardiaque:
  - DOBUTAMINE: commencer à 2 µg/kg/min
  - Augmenter par paliers 5-10 µg/kg/min

⚠️ Préférer association Noradrénaline + Dobutamine plutôt qu'Adrénaline

• Remplissage prudent: 500 mL NaCl 0,9% (si pas d'OAP ni surcharge VD)
• Intubation/Ventilation si nécessaire (CI relative: IDM VD)
• Assistance circulatoire (ECMO++)
• Traitement étiologique

**INTOXICATION PAR CARDIOTROPES:**
• Après régulation: orienter vers CHU/CH avec ECMO

⚠️ IDM contre-indication à fibrinolyse si choc cardiogénique`
    },
    {
      id: 'tamponnade',
      name: 'Tamponnade Cardiaque',
      icon: Heart,
      color: 'text-purple-400',
      bg: 'bg-purple-500/10',
      border: 'border-purple-400/30',
      content: `**Tamponnade Cardiaque - Urgence Médico-Chirurgicale Absolue**

**DÉFINITION:**
• Épanchement péricardique compressif + État de choc cardiogénique

**CLINIQUE:**
• Diminution bruits du cœur
• Dyspnée/Orthopnée (Refus décubitus dorsal)
• Oppression thoracique

**TRIADE DE BECK (rare):**
1. Choc cardiogénique
2. Turgescence jugulaire
3. Pouls paradoxal de Kussmaul (↓ inspiratoire PAS >10 mmHg)

**ECG:**
• Microvoltage diffus <0,5 mV (surtout précordial)
• Alternance électrique (variabilité intensité QRS selon respiration)
⚠️ Absence de ces signes = diagnostic possible

**RADIO THORAX:**
• Cardiomégalie

**ETT = EXAMEN DE RÉFÉRENCE**

**1. Épanchement péricardique:**
• Coupe PSGA: espace hypoéchogène entre aorte descendante, OG et VG
⚠️ Piège: épanchement pleural gauche

**2. Compression cardiaque:**
• Compression systolique OD puis diastolique VD
• Mouvements pendulaires "Swinging Heart"
• VCI dilatée >25 mm sans variations respiratoires
• Septum paradoxal à l'inspiration
• Diminution inspiratoire vélocités flux aortique/mitral

**TRAITEMENT:**
• Position demi-assise
• Expansion volémique
• Amines IVSE (pas de consensus)
• Préserver ventilation spontanée le plus longtemps possible

**ANESTHÉSIE-SÉDATION:**
⚠️ À éviter tant que possible hors bloc opératoire
• Si VS: Kétamine ou Halogénés
• Si ISR nécessaire (à éviter): Etomidate

**DRAINAGE = SEUL TRAITEMENT EFFICACE:**
Ne pas retarder!

• Péricardiocenthèse: technique Seldinger sous-xyphoïdien, repérage écho
  (Sauf situation sauvetage ACR)
• Péricardiotomie: abord chirurgical (biopsie + décaillotage)
• Sternotomie médiane: contexte post-traumatique

**TRANSPORT:**
• Médicalisé vers plateau technique adapté (cardio ou chirurgie thoracique)`
    },
    {
      id: 'dissection-aorte',
      name: 'Dissection de l\'Aorte',
      icon: Activity,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**Dissection Aortique**

**TERRAIN:**
• HTA non équilibrée
• Maladie de Marfan

**CLINIQUE:**
• Douleur intense, brutale, migratrice, parfois syncopale
• Topographie: dorsale, interscapulaire, lombaire, ou rétrosternale médiothoracique
• Asymétrie des pouls
• Anisotension: différence ≥20 mmHg TAS entre les 2 bras

**SELON LOCALISATION:**
• IDM (atteinte coronarienne)
• AVC (atteinte carotide/vertébrale)
• Ischémie aiguë MI
• Tamponnade

**SCORE PROBABILITÉ DISSECTION AORTIQUE (1 pt/item):**

**Contexte haut risque:**
• Marfan, histoire familiale, valvulopathie aortique, anévrisme connu, geste sur aorte

**Douleur haut risque:**
• Thoracique, dorsale ou abdominale, brutale, intense, déchirure

**Signes haut risque:**
• Déficit perfusion, souffle aortique nouveau, hypotension artérielle

**EXAMENS:**
• 2 VVP, O2, scope, ECG
• Prélever: groupe Rh, RAI, bilan standard, troponine, D-dimères, BH, CPK, GDS, lactates

**IMAGERIE:**
• TDM TAP injecté temps artériel
• Si allergie iode: IRM
• Si instable: ETT/ETO

**TRAITEMENT:**

**Chirurgical avant tout**

**Médical:**

**Antalgie:**
• Morphine titrée

**TENSIONNEL:**
Objectifs: TAS 100-120 mmHg, FC <60 bpm

**β-bloquant en 1ère intention (après exclusion IAo):**
• Labétalol (Trandate): 0,25-0,5 mg/kg IVD lent, répété 1 fois après 10 min
  Entretien: 0,1 mg/kg/h (max 0,3 mg/kg/h)
• Esmolol (Brévibloc): 0,5 mg/kg en charge puis 0,10-0,20 mg/kg/min

**Si CI β-Bloquant (asthme/BPCO/allergie):**
• Diltiazem (TILDIEM®): 0,25 mg/kg IV puis 5 mg/h PSE

**Si traitement antiHTA additionnel ou IAo non exclue:**
• Nicardipine (Loxen): 1 mg IVD/3 min puis 2 mg/h, augmenter par paliers 1 mg/h toutes les 15 min

⚠️ Contrôle agressif HTA et tachycardie crucial`
    },
    {
      id: 'aaa',
      name: 'Anévrisme Aorte Abdominale',
      icon: Heart,
      color: 'text-rose-400',
      bg: 'bg-rose-500/10',
      border: 'border-rose-400/30',
      content: `**Anévrisme Aorte Abdominale (AAA)**

**DÉFINITION:**
• Dilatation segmentaire aorte abdominale
• Évolution: fissuration → rupture

**SIGNES CLINIQUES:**
• Douleur abdominale ou lombaire brutale et intense
• Vagale voire syncopale
• +/- Contracture ou défense
• Masse profonde battante (rare++)
• Pouls fémoraux asymétriques
• Marbrures MI voire ischémie de membre
• +/- Choc hémorragique
• Paraplégie (atteinte artère Adamkiewicz)

**ÉCHOGRAPHIE ABDOMINALE:**
Sans retarder prise en charge!

• Voie antérieure++ ou latérale gauche
• De la xyphoïde à bifurcation iliaque
• Diamètre antéro-postérieur >3 cm = AAA
• +/- Perte parallélisme coupe longitudinale
• Recherche épanchement péritonéal (15% des cas)
⚠️ Épanchement rétropéritonéal fréquent mais difficile en écho

**TRAITEMENT:**

• O2 QSP SpO2 >95%
• Position: décubitus dorsal, Trendelenburg si choc

**ACCÈS VASCULAIRE:**
• 2 VVP gros calibre (≥16G)
• Si pas de VVP: dispositif intra-osseux

**PRÉLÈVEMENTS:**
• Groupages, RAI + bilan standard

**REMPLISSAGE:**
• NaCl 0,9% PRUDENT
• HYPOTENSION PERMISSIVE: PAS 80-90 mmHg
• Si inefficace: NORADRÉNALINE

**AUTRES:**
• Analgésie
• Anticiper demande transfusion sang
• Si fissuration sans choc + HTA: Nicardipine (Loxen)
• Appel chirurgien vasculaire précoce
• Confirmation par scanner

⚠️ URGENCE CHIRURGICALE ABSOLUE si fissuration/rupture`
    },
    {
      id: 'ep-grave',
      name: 'Embolie Pulmonaire Grave',
      icon: Wind,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      border: 'border-blue-400/30',
      content: `**Embolie Pulmonaire Grave**

**DÉFINITION:**
• EP grave = EP avec état de choc
• TAS <90 mmHg ou chute ≥40 mmHg pendant >15 min
  (hors contexte arythmie nouvelle, hypovolémie, sepsis)
• Signes périphériques de choc

**CONTEXTE:**
• Alitement, immobilisation, antécédents
• Facteurs risque MTEV, cancer, post-op...

**SIGNES CLINIQUES:**
• Dyspnée, douleur thoracique majorée inspiration, hémoptysie
• TJ, RHJ, tachycardie
• Signes TVP
• Syncope ou malaise lipothymique
• Détresse respiratoire: tachypnée >30/min, cyanose
• Sueurs, douleur angineuse
• Signes cœur pulmonaire aigu (IC droite aiguë)

**ECG:**
• Tachycardie sinusale
• Bloc de branche droit de novo
• Aspect S1Q3
• Ondes T inversées V1-V3 et DIII
⚠️ ECG normal dans 50% des cas

**ETT = CŒUR PULMONAIRE AIGU:**

**Parasternale GA ou sous-costale:**
• Dilatation VD et paroi VD non hypertrophiée
• Rapport VD/VG ≥1

**Parasternale PA:**
• Dyskinésie septale (septum aplati voire paradoxal)

**Autres signes:**
• VCI non compliante
• Bombement septum dans VG

**TRAITEMENT:**

• Position ½ assise
• O2 au MHC
• VVP

**SI ÉTAT DE CHOC + FORTE SUSPICION EP:**

**Amines:**
• NORADRÉNALINE en 1ère intention QSP PAM ≥65 mmHg
• Discuter DOBUTAMINE ou ADRÉNALINE si bas débit cardiaque

**THROMBOLYSE en urgence si instabilité hémodynamique:**
• rtPA (Actilyse): bolus 10 mg sur 1-2 min puis 90 mg en 2h (max 1,5 mg/kg si <65 kg)
• Héparine non fractionnée: bolus IV 50 UI/kg puis 20 UI/kg/h IVSE

**PRÉCAUTIONS:**
• Éviter IOT tant que possible (aggrave IVD)
• Limiter remplissage

**SI ACR SUR SUSPICION EP:**
• Thrombolyse de sauvetage
• 90 min de RCP minimum

**TRANSPORT:**
• Médicalisé en réanimation`
    },
    {
      id: 'bradycardies',
      name: 'Bradycardies',
      icon: TrendingDown,
      color: 'text-green-400',
      bg: 'bg-green-500/10',
      border: 'border-green-400/30',
      content: `**Bradycardies**

**DÉFINITION:**
• FC <60/min

**TYPES:**

**Bradycardie sinusale:**
• Rapport 1/1 entre P et QRS

**Dysfonction sinusale:**
• Disparition ondes P

**Blocs sino-atriaux:**
• BSA type 2: disparition intermittente P
• BSA type 3: disparition constante P + échappement jonctionnel

**Blocs atrio-ventriculaires:**
• BAV 2 Mobitz I (Luciani Wenchebach): allongement progressif PR
• BAV 2 Mobitz II: PR constant, P bloquées
• BAV haut degré: >2 P bloquées successives
• BAV 3 (complet): dissociation P et QRS

**ÉLÉMENTS DE GRAVITÉ:**
• Syncope d'Adams-Stokes
• Insuffisance cardiaque/coronarienne
• Choc cardiogénique

**TRAITEMENT:**

**1. TRAITEMENT ÉTIOLOGIQUE:**
Rechercher cause curable:
• Ischémie
• Dyskaliémie
• Intoxication cardiotropes (β-bloquants, digitaliques, inhibiteurs calciques)
• Hypothermie
• Hypothyroïdie
• Hypoxie
• Hypovolémie

**2. TRAITEMENT SYMPTOMATIQUE:**

**Si bien tolérée:**
• RAS

**Si mal tolérée:**

• ATROPINE 0,5-1 mg toutes les 3-5 min (max 3 mg)

• ISOPRÉNALINE IVSE:
  - 5 ampoules 0,2 mg dans 50 mL G5%
  - 0,05-0,1 µg/kg/min
  - Augmenter progressivement (objectif FC >50)
  ⚠️ Contre-indiqué si IDM

• EESE (Entraînement ÉlectroSystolique Externe Transcutané)

**TRANSPORT:**
• Haut degré ou mal tolérée = médicalisation`
    },
    {
      id: 'tachycardies',
      name: 'Tachycardies',
      icon: TrendingUp,
      color: 'text-yellow-400',
      bg: 'bg-yellow-500/10',
      border: 'border-yellow-400/30',
      content: `**Tachycardies**

**PRINCIPES:**
• ACR ou choc cardiogénique + troubles conscience → DÉFIBRILLATION (200 J biphasique)
• Cardioversion tachycardie atriale → anticoagulation par héparinothérapie
• Trouble du rythme mal toléré → ralentir ou cardioverser (chimiquement ou électriquement)

**TACHYCARDIES À QRS FINS:**

**Régulières:**
• TSV régulières, TJ
• Aide diagnostic: manœuvres vagales ou ATP

**Irrégulières:**
• Fibrillation auriculaire
• Tachycardie atriale multifocale

**Ondes P visibles?**
• OUI → Flutter, Tachy atriale focale
• NON → Nombre P>QRS?
  - OUI → AVNRT, AVRT (WPW)
  - NON → Tachy sinusale, Tachy atriale focale

**TRAITEMENT QRS FINS:**

**Manœuvres vagales:**
• Massage sinocarotidien (si pas CI)
• Valsalva
• Réflexe nauséeux

**ADÉNOSINE TRIPHOSPHATE (Striadyne®):**
• 10 mg IVD puis 20 mg si échec
• Direct au cathlon + rincer 20 cc NaCl 0,9%

**Inhibiteur calcique:**
• DILTIAZEM (Tildiem®): 0,25-0,3 mg/kg IV (~20 mg) sur 2 min

---

**TACHYCARDIES À QRS LARGES:**

**Diagnostic différentiel:**
• TV / Torsade de pointe
• Tachy QRS fin avec bloc de conduction
• FA sur voie accessoire: QRS larges empâtés, rythme irrégulier "en accordéon"

**Aide diagnostic:**
• Critères de Vereckei et Brugada
• Dissociation AV (P<QRS)
• Complexes capture/fusion

**TRAITEMENT QRS LARGES:**

**Si TV + choc + troubles conscience ou ACR:**
• CARDIOVERSION ÉLECTRIQUE

**Si TV + OAP/SCA:**
• AMIODARONE 5 mg/kg IV sur 20 min-1h puis 50 mg/h (max <1g/24h)

**Si bonne tolérance:**
• Avis cardio
• Si indication: Aténolol 1 mg/min (max 5 mg)

**Si absence critères CEE:**
• AMIODARONE 5 mg/kg IV sur 30-60 min

**Dernier recours (orage rythmique):**
• LIDOCAÏNE: 1-1,5 mg/kg IVD (~100 mg)
• Réitérer 50 mg si besoin (max 3 mg/kg)

**FA SUR VOIE ACCESSOIRE:**
⚠️ CI: β-bloquants, inhibiteurs calciques, digitaliques
• FLÉCAÏNIDE IV 1-2 mg/kg en ≥10 min
  (si pas de cardiopathie ischémique ni altération FEVG)
• Si mal tolérée: cardioversion électrique

**TRANSPORT:**
• Mal tolérée = médicalisation
• Une fois réduite: transport possiblement non médicalisé`
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-gradient-to-br from-red-500/20 to-pink-500/20 rounded-xl border border-red-400/30">
            <Heart className="w-8 h-8 text-red-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[hsl(var(--text))]">Référentiels Cardio</h1>
            <p className="text-[hsl(var(--text-dim))]">Protocoles cardiovasculaires interactifs avec analyse AI</p>
          </div>
        </div>
        
        <Alert className="bg-red-500/10 border-red-400/30">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <AlertDescription className="text-[hsl(var(--text))]">
            <strong>Urgences cardiovasculaires :</strong> Basé sur le Livret SMUR 2025 - Protocoles validés
          </AlertDescription>
        </Alert>
      </div>

      {/* Pathologies Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {pathologies.map((path) => {
          const Icon = path.icon;
          return (
            <div
              key={path.id}
              className={`p-4 rounded-lg border ${path.border} ${path.bg} hover:scale-105 transition-transform cursor-pointer`}
              onClick={() => document.getElementById(`accordion-${path.id}`)?.click()}
              data-testid={`card-${path.id}`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${path.bg} border ${path.border}`}>
                  <Icon className={`w-6 h-6 ${path.color}`} />
                </div>
                <h3 className={`font-semibold ${path.color}`}>{path.name}</h3>
              </div>
            </div>
          );
        })}
      </div>

      {/* Accordion Sections */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-cyan-400" />
            Protocoles Détaillés
          </CardTitle>
          <CardDescription>
            Cliquez sur une pathologie pour voir le protocole complet et lancer l'analyse AI
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[700px] pr-4">
            <Accordion type="single" collapsible className="w-full">
              {pathologies.map((path) => {
                const Icon = path.icon;
                const isLoading = aiLoading[path.id];
                const aiResult = aiResults[path.id];

                return (
                  <AccordionItem key={path.id} value={path.id} data-testid={`accordion-${path.id}`}>
                    <AccordionTrigger id={`accordion-${path.id}`} className="text-[hsl(var(--text))]">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${path.bg} border ${path.border}`}>
                          <Icon className={`w-5 h-5 ${path.color}`} />
                        </div>
                        <span className="font-semibold">{path.name}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-6 pt-4">
                        {/* Contenu du protocole */}
                        <div className="p-6 bg-[hsl(var(--bg-elev))] rounded-lg border border-[hsl(var(--border))]">
                          <div className="prose prose-invert max-w-none">
                            <div className="text-[hsl(var(--text))] whitespace-pre-wrap text-sm leading-relaxed">
                              {path.content}
                            </div>
                          </div>
                        </div>

                        {/* Bouton Analyse AI */}
                        <Button
                          onClick={() => handleAiAnalysis(path.id, path.name, path.content)}
                          disabled={isLoading}
                          className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
                          data-testid={`ai-button-${path.id}`}
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Analyse AI en cours...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Analyse AI de ce protocole
                            </>
                          )}
                        </Button>

                        {/* Résultat AI */}
                        {aiResult && (
                          <div className="p-6 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-400/30 rounded-lg">
                            <h3 className="text-lg font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                              <Sparkles className="w-5 h-5" />
                              Analyse AI
                            </h3>
                            <div className="prose prose-invert max-w-none">
                              <div className="text-[hsl(var(--text))] whitespace-pre-wrap text-sm" data-testid={`ai-result-${path.id}`}>
                                {aiResult}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
